import type { Query, QueryClient } from "@tanstack/react-query"

import { transport } from "@/types_api/backend/transport/operations"

const REVIEW_OVERVIEW_OPERATION = transport.operations.getReviewOverview.name

// Bridge invalidations across the additive v2 migration until v2 becomes canonical.
const REVIEW_OVERVIEW_V2_OPERATION = "getReviewOverview.v2"

const REVIEW_OVERVIEW_QUERY_KEY_PREFIXES = [
  ["transport", REVIEW_OVERVIEW_OPERATION],
  ["transport", REVIEW_OVERVIEW_V2_OPERATION],
] as const

const REVIEW_SIMULATION_COMPLETIONS_OPERATION =
  transport.operations.getReviewProjectSimulationCompletions.name

const REVIEW_ANNOTATIONS_OPERATION =
  transport.operations.listReviewAnnotations.name

const REVIEW_ANNOTATIONS_QUERY_KEY_PREFIXES = [
  ["transport", REVIEW_ANNOTATIONS_OPERATION],
  ["transport-paginated", REVIEW_ANNOTATIONS_OPERATION],
  ["transport-infinite", REVIEW_ANNOTATIONS_OPERATION],
] as const

export function invalidateReviewOverviewQueries(queryClient: QueryClient) {
  return Promise.all(
    REVIEW_OVERVIEW_QUERY_KEY_PREFIXES.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  ).then(() => undefined)
}

/**
 * Completion state lives in the overview, per-project completions, and annotation rows,
 * so marking a conversation complete has to refresh all three representations.
 */
export function invalidateReviewCompletionQueries(queryClient: QueryClient) {
  return Promise.all([
    invalidateReviewOverviewQueries(queryClient),
    invalidateReviewAnnotationQueries(queryClient),
    queryClient.invalidateQueries({
      queryKey: ["transport", REVIEW_SIMULATION_COMPLETIONS_OPERATION],
    }),
  ]).then(() => undefined)
}

const isLightweightReviewOverviewRequest = (request: unknown) => {
  if (request == null || typeof request !== "object") {
    return false
  }

  const overviewRequest = request as {
    include_linked_simulation_ids?: unknown
    include_simulation_metadata?: unknown
  }

  return (
    overviewRequest.include_linked_simulation_ids === false &&
    overviewRequest.include_simulation_metadata === false
  )
}

// Transport query keys end with the request, after the organization and workspace segments.
const getReviewOverviewQueryRequest = (query: Query): unknown => {
  const queryKey = query.queryKey
  const isReviewOverviewQuery =
    queryKey[0] === "transport" &&
    (queryKey[1] === REVIEW_OVERVIEW_OPERATION ||
      queryKey[1] === REVIEW_OVERVIEW_V2_OPERATION)
  return isReviewOverviewQuery ? queryKey.at(-1) : undefined
}

const isLightweightReviewOverviewQuery = (query: Query) =>
  isLightweightReviewOverviewRequest(getReviewOverviewQueryRequest(query))

export function invalidateLightweightReviewOverviewQueries(
  queryClient: QueryClient,
) {
  return queryClient.invalidateQueries({
    predicate: isLightweightReviewOverviewQuery,
  })
}

const requestAsksForSimulationCompletions = (request: unknown) => {
  if (request == null || typeof request !== "object") {
    return false
  }

  return (
    (request as { include_simulation_completions?: unknown })
      .include_simulation_completions === true
  )
}

const isReviewOverviewCompletionQuery = (query: Query) =>
  requestAsksForSimulationCompletions(getReviewOverviewQueryRequest(query))

/**
 * Refresh only the overview queries that carry the server-computed completion maps. The org-wide
 * counts query does not request them, so it stays cached — annotation saves should not pay for
 * refetching every project.
 */
export function invalidateReviewOverviewCompletionQueries(
  queryClient: QueryClient,
) {
  return queryClient.invalidateQueries({
    predicate: isReviewOverviewCompletionQuery,
  })
}

export function invalidateReviewAnnotationQueries(queryClient: QueryClient) {
  return Promise.all(
    REVIEW_ANNOTATIONS_QUERY_KEY_PREFIXES.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  ).then(() => undefined)
}
